/**
 * Feature-pack sync accessor for pure helpers + services that lack React
 * context — the pack analogue of `getLocaleSync()`. TenantContext caches the
 * active tenant's `featurePacks` to localStorage on load; services (e.g.
 * job-side-effects deciding whether to auto-activate customer services) read
 * it synchronously.
 *
 * Fallback rules match `resolveFeatures()`: nothing cached (or a tenant doc
 * with no `featurePacks` field) resolves to ALL features, so the existing
 * prod tenant behaves identically before the field is stamped.
 */
import {
  hasFeature,
  type FeatureKey,
} from '@shared/domain/feature-packs';
import type { TenantConfig } from '@/types/tenant.types';

const FEATURE_PACKS_STORAGE_KEY = 'tenant_feature_packs';

/** The cached pack list, or null when unset (→ all features). */
export function getFeaturePacksSync(): string[] | null {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  const raw = window.localStorage.getItem(FEATURE_PACKS_STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((p) => typeof p === 'string') : null;
  } catch {
    return null;
  }
}

/** Synchronous feature check for non-React code. */
export function hasFeatureSync(feature: FeatureKey): boolean {
  return hasFeature(getFeaturePacksSync(), feature);
}

/**
 * Cache the active tenant's featurePacks so hasFeatureSync() can read it.
 * Called by TenantContext whenever a tenant config loads/updates. A tenant
 * doc without the field CLEARS the cache (unset ≠ empty array — unset means
 * all features).
 */
export function cacheTenantFeaturePacks(
  tenant: TenantConfig | null | undefined,
): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  if (tenant?.featurePacks) {
    window.localStorage.setItem(
      FEATURE_PACKS_STORAGE_KEY,
      JSON.stringify(tenant.featurePacks),
    );
  } else {
    window.localStorage.removeItem(FEATURE_PACKS_STORAGE_KEY);
  }
}
